import { Component, effect, inject, OnInit, output, ViewChild } from '@angular/core';

import { CommonModule } from '@angular/common';
import {
  AnalyticsService,
  BackLinkComponent,
  fromMatchMedia,
  FromMatchMediaEvent,
  SaComponent
} from 'smartauction-frontend-micro-library';

import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TranslocoService } from '@jsverse/transloco';
import { TrackDirective, TrapFocusComponent } from 'remarketing-ui-components-lib';
import {
  BackLinkState,
  ConfigurationState,
  DateUtilsService,
  InventoryManagementState,
  LaunchDarklyService,
  MobileSearchMode,
  UpdateMobileSearchMode,
  UserService
} from 'remarketing-angular-library';
import { BehaviorSubject, combineLatest, EMPTY, iif, merge, Observable, of } from 'rxjs';
import {
  selectedGroupMemberUuids,
  selectHasMembers,
  unselectedMemberUuid
} from '../../services/lead-seller-group/reducer';

import { select, Store } from '@ngxs/store';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import {
  LeadSellerPreferencesService
} from '../../services/lead-seller-preferences/lead-seller-preferences.service';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeScan,
  share,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { showHomePageBackLink } from '../../../app/shared/utils/util';
import MicroModal from 'micromodal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { PreferencesPopoverComponent } from '../preferences-popover/preferences-popover.component';
import {
  MultiVinSearchModalComponent
} from '../multi-vin-search-modal/multi-vin-search-modal.component';
import {
  DealerGroupSelectionComponent
} from '../dealer-group-selection/dealer-group-selection.component';
import { DealerGroupsState } from '../../interfaces/dealer-group.interface';
import {
  HeaderSearchExecutionService
} from '../../services/header-search-execution/header-search-execution.service';
import { AccountManagerSelectionComponent } from '../account-manager/account-manager-selection/account-manager-selection.component';


@Component({
  selector: 'app-inventory-management-header',
  templateUrl: './inventory-management-header.component.html',
  styleUrl: './inventory-management-header.component.scss',
  imports: [
  CommonModule,
  BackLinkComponent,
  TabsModule,
  ReactiveFormsModule,
  DealerGroupSelectionComponent,
  TrackDirective,
  PopoverModule,
  MultiVinSearchModalComponent,
  DealerGroupSelectionComponent,
  TrapFocusComponent,
  PreferencesPopoverComponent,
  AccountManagerSelectionComponent
  ],
  standalone: true
  })
export class InventoryManagementHeaderComponent extends SaComponent implements OnInit {
  private readonly store = inject(Store);
  protected launchDarklyService = inject(LaunchDarklyService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly userService = inject(UserService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly leadSellerPreferencesService = inject(LeadSellerPreferencesService);
  private readonly leadSellerGroupService = inject(LeadSellerGroupService);
  private readonly headerSearchExecutionService = inject(HeaderSearchExecutionService);
  private formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  protected readonly inventorySearchCriteria$ = select(InventoryManagementState.searchCriteria);
  mobileSearchModeEnum = MobileSearchMode;
  hasLeadSellerMembers$;
  hasPreferencesMembers$;
  isDesktop = new BehaviorSubject<FromMatchMediaEvent>(null);
  isDesktop$ = fromMatchMedia('(min-width: 769px)');
  searchTrackName$: Observable<string>;
  searchForm: UntypedFormGroup;
  userProfile;
  showBackLink: boolean;
  preferencesPreState: DealerGroupsState;
  disableFocus = false;

  translations = {
    textManageInventoryHeader: '',
    textSearchTabLabel: '',
    textAdvancedSearchTabLabel: '',
    textGroupDropdownLabel: '',
    textSearchByLabel: '',
    textSearchByVinLabel: '',
    textVinInputPlaceholder: '',
    textEnterValidVinMessage: '',
    textOr: '',
    textSearchBySellerLegacyIdLabel: '',
    textSellerLegacyIdInputPlaceholder: '',
    textEnterValidSellerLegacyIdMessage: '',
    textSearchByStockNumberLabel: '',
    textSearchButtonLabel: '',
    textSearchByVinEnterMinimum: '',
    textVinLabel: '',
    textSellerLegacyIdLabel: '',
    textStockUnitLabel: '',
    textAriaLabelStockNumber: '',
    textEditPreference: '',
    textDefaultPreferences: '',
    textSavePreferences: '',
    textButtonClose: '',
    multiVinDialogHeadlineMsg: '',
    labelAccountManager: ''
  };

  protected readonly mobileSearchMode$ = select(InventoryManagementState.mobileSearchMode);

  // signal style did not work with preferencesPopover
  @ViewChild('pop') preferencePopover;

  constructor() {
    super();
    this.initializeSearchForm();

    effect(() => {
      const searchCriteria = this.inventorySearchCriteria$();
      // If exactly 1 search value, we will show that in main search bar.
      // We will not show multi vin search values in main search bar
      const displayVin = searchCriteria?.vins?.length === 1 ? searchCriteria.vins[0] : '';
      const displayStockNumber = searchCriteria?.stockNumbers?.length === 1 ? searchCriteria.stockNumbers[0] : '';
      const displaySellerLegacyId = searchCriteria?.sellerLegacyId ? searchCriteria.sellerLegacyId : null;
      this.searchForm.patchValue({
        vin: displayVin,
        stockNum: displayStockNumber,
        sellerLegacyId: displaySellerLegacyId
      });
    });
  }

  ngOnInit() {
    this.getTranslations();
    this.initializeHomepageBacklinkSubscription();
    this.initializeUserLeadSellerPreferences();
    this.setupFormValueFormatting();
    this.initializeResponsiveDesktopSubscription();
    this.initializeSearchAnalytics();
    this.hasLeadSellerMembers$ = this.leadSellerGroupService.select(selectHasMembers);
    this.hasPreferencesMembers$ = this.leadSellerPreferencesService.select(selectHasMembers);
  }

  initializeHomepageBacklinkSubscription(): void {
    this.store
      .select(BackLinkState.backLinkState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(state => {
        this.showBackLink = showHomePageBackLink(state);
      });
  }

  initializeUserLeadSellerPreferences(): void {
    this.store
      .select(state => state.user.userProfile)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(profile => {
        if (profile) {
          this.userProfile = profile;
        }
        const leadSellerExcludedDealers =
          this.userProfile && this.userProfile.userPreferences.leadSellerExcludedDealers
            ? this.userProfile.userPreferences.leadSellerExcludedDealers
            : [];
        this.leadSellerGroupService.loadLeadSellerGroups(leadSellerExcludedDealers);
      });
  }

  initializeSearchForm(): void {
    function StockNumLengthValidator(control: AbstractControl) {
      const stockNum = control.value.trim().replace(/[\s,]+/gi, '');
      if (stockNum.length > 12) {
        return true;
      }
      return null;
    }

    this.searchForm = this.formBuilder.group({
      vin: ['', [Validators.minLength(6), Validators.maxLength(17)]],
      stockNum: ['', [StockNumLengthValidator]],
      sellerLegacyId: [null, [Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  private setupFormValueFormatting(): void {
    const formattedVin$ = this.createFormattedVinStream();
    const formattedStockNum$ = this.createFormattedStockNumStream();
    const formattedSellerLegacyId$ = this.createFormattedSellerLegacyIdStream();

    merge(formattedVin$, formattedStockNum$, formattedSellerLegacyId$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  private createFormattedVinStream(): Observable<string> {
    const vinCtrl = this.searchForm.get('vin');

    if (!vinCtrl) {
      return of(''); // or throw meaningful error
    }

    return vinCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      map<string, string>(val => val?.trim() || ''),
      mergeScan((lastGoodValue, value) => {
        if (value.length > 17 && lastGoodValue.length === 0) {
          return of(value.slice(0, 17));
        }
        if (value.length > 17) {
          return of(lastGoodValue);
        }
        return of(value);
      }, ''),
      tap(val => vinCtrl.setValue(val, { emitEvent: false }))
    );
  }

  private createFormattedStockNumStream(): Observable<string> {
    const stockNumCtrl = this.searchForm.get('stockNum');

    if (!stockNumCtrl) {
      return of('');
    }

    return stockNumCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      map(val => {
        if (!val) return '';

        const trimmedStockValue = val.trim().replace(/[\s,]/gi, '');
        const spaceCount = val.split(' ').length - 1;

        if (trimmedStockValue.length >= 12) {
          return val.slice(0, 12 + spaceCount).trim();
        }
        return val;
      }),
      tap(val => stockNumCtrl.setValue(val, { emitEvent: false }))
    );
  }

  private createFormattedSellerLegacyIdStream(): Observable<number> {
    const sellerLegacyIdCtrl = this.searchForm.get('sellerLegacyId');

    if (!sellerLegacyIdCtrl) {
      return EMPTY;
    }

    return sellerLegacyIdCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      map<number, number>(val => val?.valueOf()),
      tap(val => sellerLegacyIdCtrl.setValue(val, { emitEvent: false }))
    );
  }

  private initializeResponsiveDesktopSubscription(): void {
    this.isDesktop$.pipe(takeUntil(this.destroyed$)).subscribe(matches => this.isDesktop.next(matches));
  }

  private initializeSearchAnalytics(): void {
    const formattedVin$ = this.createFormattedVinStream();
    const formattedStockNum$ = this.createFormattedStockNumStream();
    const formattedSellerLegacyId$ = this.createFormattedSellerLegacyIdStream();

    this.searchTrackName$ = this.isDesktop$.pipe(
      map(({ matches }) => matches),
      switchMap(isDesktop =>
        this.createSearchValuesStream(formattedVin$, formattedStockNum$, formattedSellerLegacyId$, isDesktop)
      ),
      map(searchType => `${searchType} - search`),
      share()
    );
  }

  private createSearchValuesStream(
    formattedVin$: Observable<string>,
    formattedStockNum$: Observable<string>,
    formattedSellerLegacyId$: Observable<number>,
    isDesktop: boolean
  ): Observable<string> {
    const searchValues$ = combineLatest([
      formattedVin$.pipe(startWith('')),
      formattedStockNum$.pipe(startWith('')),
      formattedSellerLegacyId$.pipe(startWith(Number.NaN)),
      this.mobileSearchMode$()
    ]);

    return iif(
      () => isDesktop,
      searchValues$.pipe(
        map(([vin, stockNumber, sellerLegacyId]) => desktopSearchType(vin, stockNumber, sellerLegacyId))
      ),
      searchValues$.pipe(
        map(([vin, stockNumber, sellerLegacyId, mobileSearchMode]) =>
          mobileSearchType(vin, stockNumber, sellerLegacyId, this.mobileSearchMode$())
        )
      )
    );
  }

  setMobileSearchMode(selectedSearchMode: MobileSearchMode): void {
    this.store.dispatch(new UpdateMobileSearchMode(selectedSearchMode));
    this.analyticsTrackSearchMode(selectedSearchMode);
  }

  analyticsTrackSearchMode(mobileSearchMode: MobileSearchMode): void {
    const searchMode =
      this.inServiceVehiclesEnabled && mobileSearchMode === MobileSearchMode.STOCKNUMBER ? 'mva' : mobileSearchMode;

    this.analyticsService.trackEvent('radioButton', searchMode);
  }

  search(): void {
    const vin = this.searchForm.get('vin').value.trim();
    const stockNumber = this.searchForm.get('stockNum').value.trim();
    const sellerLegacyId = this.searchForm.get('sellerLegacyId').value;
    const isDesktop = this.isDesktop?.getValue()?.matches;

    if (this.searchForm.valid) {
      const vinsForPayload = isDesktop || this.mobileSearchMode$() === MobileSearchMode.VIN ? [vin] : [];

      const stockNumbersForPayload =
        isDesktop || this.mobileSearchMode$() === MobileSearchMode.STOCKNUMBER ? [stockNumber] : [];

      const sellerLegacyIdForPayload =
        this.isSellerLegacyIdSearchEnabled &&
        (isDesktop || this.mobileSearchMode$() === MobileSearchMode.SELLERLEGACYID)
          ? sellerLegacyId
          : null;

      this.headerSearchExecutionService.executeCommonSearchBarTextInputSearch(
        vinsForPayload,
        stockNumbersForPayload,
        sellerLegacyIdForPayload,
        isDesktop
      );

      this.analyticsFormSuccess(vin, stockNumber);
    } else {
      this.handleInvalidSearchFormAnalytics(vin, stockNumber, sellerLegacyId);
    }
  }

  openMicroModal(modalId: string) {
    MicroModal.show(modalId, {
      disableScroll: true
    });
    const heading = document.querySelector(`#${modalId} h1`) as HTMLElement;
    if (heading) {
      heading.focus();
    }
  }

  get isSellerLegacyIdSearchEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-sellerLegacyIdSearchEnabled', false);
  }

  get isActionLinksEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-advancedSearchEnabled', false);
  }

  get isAdmin(): boolean {
    return this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  get inServiceVehiclesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get isEmployeeFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sim-employeeFilterEnabled', false);
  }

  analyticsFormSuccess(vin, stockNumber): void {
    this.analyticsService.track(
      'customEvent',
      this.analyticsService.formSubmit('formSuccess', 'formValidationSuccess', 'searchForm', [
        {
          track: 'yes',
          name: 'searchVinInput',
          value: vin !== '' ? vin : null,
          isPII: 'false'
        },
        {
          track: 'yes',
          name: this.analyticsMvaOrStockReference(),
          value: stockNumber !== '' ? stockNumber : null,
          isPII: 'false'
        }
      ])
    );
  }

  analyticsMvaOrStockReference(): string {
    return this.inServiceVehiclesEnabled ? 'searchMvaNumInput' : 'searchStockNumInput';
  }

  handleInvalidSearchFormAnalytics(vin: string, stockNumber: string, sellerLegacyId): void {
    this.analyticsService.track(
      'customEvent',
      this.analyticsService.formSubmit(
        'formError',
        'formValidationError',
        'searchForm',
        [
          {
            track: 'yes',
            name: 'searchVinInput',
            value: vin !== '' ? vin : null,
            isPII: 'false'
          },
          {
            track: 'yes',
            name: this.analyticsMvaOrStockReference(),
            value: stockNumber !== '' ? stockNumber : null,
            isPII: 'false'
          },
          {
            track: 'yes',
            name: 'searchSellerLegacyIdInput',
            value: sellerLegacyId !== null ? sellerLegacyId : null,
            isPII: 'false'
          }
        ],
        'Please enter a valid VIN.'
      )
    );
  }

  getTranslations() {
    const keyParams = {
      'sellModule.manageInventory.manageHeader': {},
      'sellModule.manageInventory.multiVinModal': {},
      common: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translation => {
        this.translations.textManageInventoryHeader = translation[0].textManageInventory;
        this.translations.textSearchTabLabel = translation[0].tabSearch;
        this.translations.textAdvancedSearchTabLabel = translation[0].tabAdvancedSearch;
        this.translations.textGroupDropdownLabel = translation[0].textGroup;
        this.translations.textSearchByVinLabel = translation[0].textSearchBy;
        this.translations.textVinInputPlaceholder = translation[0].placeholderFull;
        this.translations.textEnterValidVinMessage = translation[0].textEnterValidVIN;
        this.translations.textOr = translation[0].textOr;
        this.translations.textSearchByLabel = translation[0].textSearchBY;
        this.translations.textSearchBySellerLegacyIdLabel = translation[0].textSearchBySellerLegacyId;
        this.translations.textSellerLegacyIdInputPlaceholder = translation[0].placeholderSellerLegacyId;
        this.translations.textEnterValidSellerLegacyIdMessage = translation[0].textEnterValidSellerLegacyId;
        this.translations.textSearchByStockNumberLabel = translation[0].textSearchByStock;
        this.translations.textSearchButtonLabel = translation[0].tabSearch;
        this.translations.textSearchByVinEnterMinimum = translation[0].textEnterMinimum;
        this.translations.textVinLabel = translation[0].textVIN;
        this.translations.textSellerLegacyIdLabel = translation[0].textSellerLegacyId;
        this.translations.textStockUnitLabel = translation[0].textStockUnit;
        this.translations.textAriaLabelStockNumber = translation[0].ariaLabelStockNumber;
        this.translations.textEditPreference = translation[0].textEditPreference;
        this.translations.textDefaultPreferences = translation[0].textDefaultPreferences;
        this.translations.textSavePreferences = translation[0].textSavePreferences;
        this.translations.labelAccountManager = translation[0].labelAccountManager;
        this.translations.multiVinDialogHeadlineMsg = translation[1].dialogHeadlineMsg;
        this.translations.textButtonClose = translation[2].btnClose;
      });
  }

  onShowPreferencesPop() {
    this.leadSellerPreferencesService.state$.pipe(take(1)).subscribe(state => (this.preferencesPreState = state));
  }

  onHidePreferencesPop() {
    if (this.preferencesPreState !== null) {
      this.leadSellerPreferencesService.setState(this.preferencesPreState);
    }
  }

  savePreferences(): void {
    this.preferencesPreState = null;
    this.preferencePopover.hide();

    const currentState = this.leadSellerPreferencesService.snapShot();
    this.headerSearchExecutionService.executeSearchOnLeadSellerGroupMemberChange(
      selectedGroupMemberUuids(currentState)
    );

    this.leadSellerGroupService.setState(currentState);
    this.leadSellerPreferencesService.setState(currentState);
    this.leadSellerGroupService.cacheState();
    this.updateUserPreferences(currentState);
  }

  updateUserPreferences(state) {
    const leadSellerExcludedDealers = unselectedMemberUuid(state);

    const payload = {
      ...this.userProfile,
      userPreferences: {
        ...this.userProfile.userPreferences,
        leadSellerExcludedDealers: leadSellerExcludedDealers.length === 0 ? null : leadSellerExcludedDealers
      }
    };

    this.userService.updateUser(this.userProfile.userUuid, payload).subscribe(
      updatedUser => {
        const currentState = this.store.selectSnapshot(state => state);
        this.store.reset({
          ...currentState,
          user: {
            ...currentState.user,
            userProfile: {
              ...this.userProfile,
              userPreferences: {
                ...this.userProfile.userPreferences,
                leadSellerExcludedDealers: updatedUser.userPreferences.leadSellerExcludedDealers
              }
            }
          }
        });
      },
      error => {
        console.error('error: ', error);
      }
    );
  }
}

export const enum SellSearchType {
  VIN = 'VIN',
  STOCK_NUMBER = 'Stock Number',
  SELLER_LEGACY_ID = 'sellerLegacyId',
  BLANK = 'Blank'
}

export function desktopSearchType(vin: string, stockNumber: string, sellerLegacyId: number): SellSearchType {
  if (vin?.trim()) return SellSearchType.VIN;
  if (stockNumber?.trim()) return SellSearchType.STOCK_NUMBER;
  if (sellerLegacyId?.toString().length === 6) return SellSearchType.SELLER_LEGACY_ID;
  return SellSearchType.BLANK;
}

export function mobileSearchType(
  vin: string,
  stockNumber: string,
  sellerLegacyId: number,
  mobileSearch: 'vin' | 'stock' | 'sellerLegacyId'
): SellSearchType {
  const searchValidators = {
    vin: () => vin?.trim().length > 0,
    stock: () => stockNumber?.trim().length > 0,
    sellerLegacyId: () => sellerLegacyId?.toString().length === 6
  } as const;

  const searchTypeMap = {
    vin: SellSearchType.VIN,
    stock: SellSearchType.STOCK_NUMBER,
    sellerLegacyId: SellSearchType.SELLER_LEGACY_ID
  } as const;

  const validator = searchValidators[mobileSearch];
  return validator?.() ? searchTypeMap[mobileSearch] : SellSearchType.BLANK;
}
